<template>
  <!-- Import an audit plan (PDF/Word) into a control template with the AI (US 9404):
       upload → read in the background → map columns → review → save -->
  <b-modal ref="dialog"
           modal-class="v2-modal api-modal"
           size="xl"
           no-close-on-backdrop
           hide-footer
           qid="audit-plan-import-dialog"
           @hidden="onHidden"
  >
    <template #modal-header>
      <div class="v2-modal__head api-head">
        <span class="api-head__icon"><font-awesome-icon icon="sparkles"/></span>
        <div class="v2-modal__titles">
          <h3>{{ $t('audit_plan_import.title') }}</h3>
          <div class="v2-modal__eyebrow">{{ subtitle }}</div>
        </div>
        <ol class="api-steps">
          <li v-for="(step, index) in steps"
              :key="step"
              class="api-step"
              :class="{ 'is-on': index === stepIndex, 'is-done': index < stepIndex }"
          >
            <b><font-awesome-icon v-if="index < stepIndex" icon="check"/><template v-else>{{ index + 1 }}</template></b>{{ $t('audit_plan_import.steps.' + step) }}
          </li>
        </ol>
        <button type="button" class="us-icon-btn" :aria-label="$t('audit_plan_import.close')" qid="audit-plan-import-close" @click="close">
          <font-awesome-icon icon="times"/>
        </button>
      </div>
    </template>

    <b-alert v-if="error" show variant="danger" qid="audit-plan-import-error">{{ error }}</b-alert>

    <!-- 1. Upload -->
    <div v-if="step === 'upload'" class="api-upload" qid="audit-plan-upload">
      <div v-if="file" class="api-file">
        <span class="api-file__icon"><font-awesome-icon :icon="isWord(file.name) ? 'file-word' : 'file-alt'"/></span>
        <span class="api-file__name">{{ file.name }}<small>{{ fileSize(file.size) }}</small></span>
        <button type="button" class="btn btn-link" qid="audit-plan-upload-remove" @click="file = null">
          <font-awesome-icon icon="times" class="mr-1"/>{{ $t('audit_plan_import.upload.remove') }}
        </button>
      </div>
      <label class="api-drop"
             :class="{ 'is-over': dragOver, 'api-drop--small': file }"
             qid="audit-plan-upload-drop"
             @dragover.prevent="dragOver = true"
             @dragleave.prevent="dragOver = false"
             @drop.prevent="drop"
      >
        <input type="file" accept=".pdf,.doc,.docx" class="d-none" qid="audit-plan-upload-input" @change="pick">
        <span class="api-drop__icon"><font-awesome-icon icon="cloud-upload-alt"/></span>
        <span>
          <b>{{ $t(file ? 'audit_plan_import.upload.replace' : 'audit_plan_import.upload.drop') }}</b>
          <small>{{ $t('audit_plan_import.upload.types') }}</small>
        </span>
      </label>

      <div v-if="hasControls" class="api-option">
        <span class="us-field-label">{{ $t('audit_plan_import.upload.mode') }}</span>
        <div class="api-radios">
          <label v-for="mode in modes"
                 :key="mode"
                 class="api-radio"
                 :class="{ 'is-on': form.mode === mode, 'is-disabled': mode === 'replace' && inUse }"
                 :qid="'audit-plan-upload-mode-' + mode"
          >
            <input v-model="form.mode" type="radio" :value="mode" :disabled="mode === 'replace' && inUse">
            <span>
              <b>{{ $t('audit_plan_import.upload.mode_' + mode) }}</b>
              <small>{{ $t(mode === 'replace' && inUse ? 'audit_plan_import.upload.mode_replace_in_use' : 'audit_plan_import.upload.mode_' + mode + '_text') }}</small>
            </span>
          </label>
        </div>
      </div>

      <div class="api-option-row">
        <div class="api-option">
          <label class="us-field-label" for="api-pages">{{ $t('audit_plan_import.upload.pages') }}</label>
          <b-form-select id="api-pages" v-model="form.pagesMode" :options="pageModes" qid="audit-plan-upload-pages"></b-form-select>
          <div v-if="form.pagesMode === 'given'" class="api-pages">
            <b-form-input v-model.number="form.pagesFrom" type="number" min="1" :placeholder="$t('audit_plan_import.upload.from')" qid="audit-plan-upload-pages-from"></b-form-input>
            <span>–</span>
            <b-form-input v-model.number="form.pagesTo" type="number" min="1" :placeholder="$t('audit_plan_import.upload.to')" qid="audit-plan-upload-pages-to"></b-form-input>
          </div>
        </div>
        <div class="api-option">
          <label class="us-field-label" for="api-language">{{ $t('audit_plan_import.upload.language') }}</label>
          <b-form-select id="api-language" v-model="form.language" :options="languages" qid="audit-plan-upload-language"></b-form-select>
          <small class="us-muted">{{ $t('audit_plan_import.upload.language_text') }}</small>
        </div>
      </div>

      <div class="us-note us-note--info">
        <font-awesome-icon icon="lock"/>
        <span>{{ $t('audit_plan_import.upload.privacy') }}</span>
      </div>

      <div class="v2-modal__foot api-foot">
        <span class="api-foot__info"><font-awesome-icon icon="clock"/>{{ $t('audit_plan_import.upload.time') }}</span>
        <b-button variant="secondary" qid="audit-plan-upload-cancel" @click="close">{{ $t('audit_plan_import.cancel') }}</b-button>
        <b-button variant="success" :disabled="!canUpload || busy" qid="audit-plan-upload-submit" @click="upload">
          <font-awesome-icon :icon="busy ? 'spinner' : 'sparkles'" :class="{ 'fa-spin': busy }" class="mr-1"/>{{ $t('audit_plan_import.upload.submit') }}
        </b-button>
      </div>
    </div>

    <!-- 2. Reading -->
    <div v-else-if="step === 'reading'" class="api-reading" qid="audit-plan-reading">
      <div class="api-reading__head">
        <b>{{ $t('audit_plan_import.reading.title') }}</b>
        <span class="us-muted">{{ $t('audit_plan_import.reading.text') }}</span>
      </div>
      <div class="api-progress"><i :style="{ width: percent + '%' }"></i></div>
      <div class="api-stats">
        <div><b>{{ progress.pages || '–' }}</b>{{ $t('audit_plan_import.reading.pages') }}</div>
        <div><b>{{ progress.objectives || 0 }}</b>{{ $t('audit_plan_import.reading.objectives') }}</div>
        <div><b>{{ progress.controls || 0 }}</b>{{ $t('audit_plan_import.reading.controls') }}</div>
      </div>
      <ul class="api-feed">
        <li v-for="item in feed" :key="item.key" class="api-feed__item" :class="'is-' + item.state">
          <span class="api-feed__dot">
            <font-awesome-icon v-if="item.state === 'done'" icon="check"/>
            <font-awesome-icon v-else-if="item.state === 'running'" icon="spinner" class="fa-spin"/>
          </span>
          <span>{{ item.text }}</span>
        </li>
      </ul>
      <div class="us-note us-note--info">
        <font-awesome-icon icon="clock"/>
        <span>{{ $t('audit_plan_import.reading.background') }}</span>
      </div>
      <div class="v2-modal__foot api-foot">
        <span class="api-foot__info"><font-awesome-icon icon="lock"/>{{ $t('audit_plan_import.reading.nothing_saved') }}</span>
        <b-button variant="secondary" :disabled="busy" qid="audit-plan-reading-cancel" @click="cancelImport">{{ $t('audit_plan_import.reading.cancel') }}</b-button>
        <b-button variant="secondary" qid="audit-plan-reading-background" @click="close">{{ $t('audit_plan_import.reading.close') }}</b-button>
      </div>
    </div>

    <!-- Reading failed -->
    <div v-else-if="step === 'failed'" class="api-failed" qid="audit-plan-failed">
      <span class="api-failed__icon"><font-awesome-icon icon="exclamation-triangle"/></span>
      <b>{{ $t('audit_plan_import.failed.title') }}</b>
      <span class="us-muted">{{ $t('audit_plan_import.failed.' + failedReason) }}</span>
      <div class="v2-modal__foot api-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" @click="close">{{ $t('audit_plan_import.close') }}</b-button>
        <b-button variant="success" qid="audit-plan-failed-retry" @click="restart">{{ $t('audit_plan_import.failed.retry') }}</b-button>
      </div>
    </div>

    <!-- 3. Map columns -->
    <div v-else-if="step === 'mapping'">
      <ImportMapping :result="result" :settings="settings" :existing="existing" @change="changeSettings"></ImportMapping>
      <div class="v2-modal__foot api-foot">
        <span class="api-foot__info"><font-awesome-icon icon="sparkles"/>{{ $t('audit_plan_import.mapping.instant') }}</span>
        <b-button variant="secondary" :disabled="busy" qid="audit-plan-mapping-discard" @click="cancelImport">{{ $t('audit_plan_import.discard') }}</b-button>
        <b-button variant="success" qid="audit-plan-mapping-next" @click="step = 'review'">
          {{ $tc('audit_plan_import.mapping.next', totalControls, { count: totalControls }) }}<font-awesome-icon icon="arrow-right" class="ml-1"/>
        </b-button>
      </div>
    </div>

    <!-- 4. Review -->
    <div v-else-if="step === 'review'">
      <ImportReview :objectives="reviewed"
                    :problems="problemList"
                    :fields="mappedFields"
                    :selected="selected"
                    :ai-name="aiNames"
                    @select="selected = $event"
                    @toggle="toggle"
                    @edit="edit"
      ></ImportReview>
      <div class="v2-modal__foot api-foot">
        <span class="api-foot__info" :class="{ 'is-problem': problemCount }">
          <font-awesome-icon :icon="problemCount ? 'exclamation-triangle' : 'check'"/>
          {{ problemCount ? $tc('audit_plan_import.review.problems', problemCount, { count: problemCount }) : $tc('audit_plan_import.review.flagged', flaggedCount, { count: flaggedCount }) }}
        </span>
        <b-button variant="secondary" qid="audit-plan-review-back" @click="step = 'mapping'">
          <font-awesome-icon icon="chevron-left" class="mr-1"/>{{ $t('audit_plan_import.review.back') }}
        </b-button>
        <b-button variant="success" :disabled="busy || problemCount > 0 || !saveCounts.controls" qid="audit-plan-review-save" @click="save">
          <font-awesome-icon :icon="busy ? 'spinner' : 'check'" :class="{ 'fa-spin': busy }" class="mr-1"/>
          {{ $t('audit_plan_import.review.save', { objectives: saveCounts.objectives, controls: saveCounts.controls }) }}
        </b-button>
      </div>
    </div>

    <div v-else class="text-center text-muted p-4"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
  </b-modal>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import ImportMapping from './ImportMapping.vue';
  import ImportReview from './ImportReview.vue';
  import { applyMapping, defaultSettings, withEdits, problems, payload, NAME_AI } from './auditPlanMapping.js';

  const POLL_MS = 3000;
  const MAX_MB = 30;
  const OPEN_STATUSES = ['pending', 'reading', 'ready'];

  // The open import of a template is remembered in this browser, so closing the dialog doesn't lose it
  function storageKey(assessment) {
    return 'audit-plan-import:' + assessment.slug;
  }
  function remembered(assessment) {
    try {
      return window.localStorage.getItem(storageKey(assessment));
    } catch (e) {
      return null;
    }
  }
  function remember(assessment, id) {
    try {
      if (id) {
        window.localStorage.setItem(storageKey(assessment), id);
      } else {
        window.localStorage.removeItem(storageKey(assessment));
      }
    } catch (e) {
      // Private windows can refuse storage; the import then only lives while the dialog is open
    }
  }

  export default {
    name: 'AuditPlanImportDialog',
    components: {
      ImportMapping,
      ImportReview
    },
    props: {
      assessment: {
        type: Object,
        required: true
      },
      // Whether the template has controls (else there is nothing to add to or replace)
      hasControls: {
        type: Boolean,
        default: false
      },
      // Whether projects use the template (then it can only be added to)
      inUse: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        step: null,
        steps: ['upload', 'reading', 'mapping', 'review'],
        modes: ['add', 'replace'],
        file: null,
        dragOver: false,
        form: this.emptyForm(),
        importItem: null,
        result: null,
        settings: null,
        edits: {},
        off: {},
        selected: null,
        error: '',
        busy: false,
        poller: null
      };
    },
    computed: {
      stepIndex() {
        return this.step === 'failed' ? 1 : this.steps.indexOf(this.step);
      },
      subtitle() {
        if (this.importItem && this.importItem.file) {
          let document = this.result ? this.result.document : null;
          return this.importItem.file.name + (document ? ' · ' + this.$t('audit_plan_import.mapping.pages', { from: document.from, to: document.to }) : '');
        }
        return this.$t('audit_plan_import.subtitle');
      },
      pageModes() {
        return [
          { value: 'find', text: this.$t('audit_plan_import.upload.pages_find') },
          { value: 'given', text: this.$t('audit_plan_import.upload.pages_given') }
        ];
      },
      languages() {
        return ['', 'en', 'da', 'de', 'fr'].map(code => ({ value: code, text: this.$t('audit_plan_import.upload.languages.' + (code || 'keep')) }));
      },
      canUpload() {
        if (!this.file) {
          return false;
        }
        return this.form.pagesMode !== 'given' || (this.form.pagesFrom >= 1 && this.form.pagesTo >= this.form.pagesFrom);
      },
      progress() {
        return (this.importItem && this.importItem.progress) || {};
      },
      percent() {
        let progress = this.progress;
        if (progress.step === 'done') {
          return 100;
        }
        if (progress.step === 'extracting') {
          return Math.round(25 + 70 * progress.chunk / progress.chunks);
        }
        return { started: 5, read: 12, located: 25 }[progress.step] || 2;
      },
      feed() {
        let progress = this.progress;
        let order = ['started', 'read', 'located', 'extracting', 'done'];
        let at = order.indexOf(progress.step);
        let state = index => at > index ? 'done' : at === index ? 'running' : 'waiting';
        return [
          { key: 'read', state: at >= 1 ? 'done' : at === 0 ? 'running' : 'waiting',
            text: progress.pages ? this.$t('audit_plan_import.reading.read', { pages: progress.pages }) : this.$t('audit_plan_import.reading.reading') },
          { key: 'located', state: at >= 2 ? 'done' : at === 1 ? 'running' : 'waiting',
            text: progress.from ? this.$t('audit_plan_import.reading.located', { from: progress.from, to: progress.to, columns: progress.columns }) : this.$t('audit_plan_import.reading.locating') },
          { key: 'extracting', state: state(3),
            text: progress.chunks ? this.$t('audit_plan_import.reading.extracting', { chunk: progress.chunk, chunks: progress.chunks }) : this.$t('audit_plan_import.reading.extract') }
        ];
      },
      failedReason() {
        let known = ['file_type', 'unreadable_pdf', 'too_many_pages', 'no_audit_plan', 'no_controls'];
        return this.importItem && known.indexOf(this.importItem.error) !== -1 ? this.importItem.error : 'failed';
      },
      // References and objectives already in the template the import adds to
      existing() {
        return this.importItem ? this.importItem.existing : null;
      },
      mapped() {
        return this.result && this.settings ? applyMapping(this.result, this.settings, this.existing) : [];
      },
      reviewed() {
        return withEdits(this.mapped, this.edits, this.off);
      },
      problemList() {
        return problems(this.reviewed, this.existing ? this.existing.references : []);
      },
      problemCount() {
        return Object.keys(this.problemList).length;
      },
      totalControls() {
        return this.result ? this.result.stats.controls : 0;
      },
      flaggedCount() {
        return [].concat(...this.reviewed.map(objective => objective.controls)).filter(control => control.flags.length).length;
      },
      saveCounts() {
        let objectives = payload(this.reviewed);
        return { objectives: objectives.length, controls: objectives.reduce((count, objective) => count + objective.controls.length, 0) };
      },
      mappedFields() {
        if (!this.settings) {
          return [];
        }
        return Object.keys(this.settings.fields).filter(field => this.settings.fields[field]);
      },
      aiNames() {
        return !!this.settings && this.settings.fields.name === NAME_AI;
      }
    },
    methods: {
      emptyForm() {
        return { mode: 'add', pagesMode: 'find', pagesFrom: null, pagesTo: null, language: '' };
      },
      // Opens the dialog on the given import (from a notification), or goes on with this template's open import
      open(importId) {
        this.error = '';
        this.$refs.dialog.show();
        if (importId && String(importId) !== String(this.importItem && this.importItem.id)) {
          remember(this.assessment, importId);
          this.result = null;
          Object.assign(this, { settings: null, edits: {}, off: {}, selected: null });
        }
        let id = remembered(this.assessment);
        if (id) {
          this.step = null;
          this.load(id);
        } else {
          this.restart();
        }
      },
      close() {
        this.$refs.dialog.hide();
      },
      onHidden() {
        this.stopPolling();
        this.busy = false;
      },
      restart() {
        remember(this.assessment, null);
        this.stopPolling();
        Object.assign(this, { step: 'upload', file: null, form: this.emptyForm(), importItem: null, result: null, settings: null, edits: {}, off: {}, selected: null, error: '' });
      },
      isWord(name) {
        return /\.docx?$/i.test(name || '');
      },
      fileSize(bytes) {
        return bytes > 1048576 ? (bytes / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(bytes / 1024)) + ' KB';
      },
      pick(event) {
        this.setFile(event.target.files[0]);
        event.target.value = '';
      },
      drop(event) {
        this.dragOver = false;
        this.setFile(event.dataTransfer.files[0]);
      },
      setFile(file) {
        this.error = '';
        if (!file) {
          return;
        }
        if (!/\.(pdf|docx?)$/i.test(file.name)) {
          this.error = this.$t('audit_plan_import.failed.file_type');
          return;
        }
        if (file.size > MAX_MB * 1048576) {
          this.error = this.$t('audit_plan_import.upload.too_big', { size: MAX_MB });
          return;
        }
        this.file = file;
      },
      upload() {
        let self = this;
        let formData = new FormData();
        formData.append('file', this.file);
        formData.append('mode', this.hasControls ? this.form.mode : 'add');
        if (this.form.language) {
          formData.append('language', this.form.language);
        }
        if (this.form.pagesMode === 'given') {
          formData.append('pages_from', this.form.pagesFrom);
          formData.append('pages_to', this.form.pagesTo);
        }
        this.busy = true;
        this.error = '';
        piincHttp.post('assessments/' + this.assessment.slug + '/imports', formData).then(function(response) {
          self.busy = false;
          remember(self.assessment, response.data.id);
          self.show(response.data);
        }, function(error) {
          self.busy = false;
          self.error = self.errorText(error);
        });
      },
      load(id) {
        let self = this;
        piincHttp.get('assessments/' + this.assessment.slug + '/imports/' + id).then(function(response) {
          self.show(response.data);
        }, function(error) {
          // Gone (the template was deleted, or another template's import): start over. Other errors are
          // usually a short network problem, so polling just tries again.
          if (error.response && error.response.status === 404) {
            self.restart();
          } else if (!self.poller) {
            self.error = self.errorText(error);
          }
        });
      },
      // Shows the step that fits the import's status
      show(item) {
        this.importItem = item;
        if (OPEN_STATUSES.indexOf(item.status) === -1 && item.status !== 'failed') {
          this.restart();
          return;
        }
        if (item.status === 'failed') {
          remember(this.assessment, null);
          this.stopPolling();
          this.step = 'failed';
        } else if (item.status === 'ready') {
          this.stopPolling();
          if (!this.result && item.result) {
            this.result = item.result;
            this.settings = defaultSettings(item.result);
            this.off = this.defaultOff(item.result);
            this.selected = item.result.objectives.length && item.result.objectives[0].controls.length ? item.result.objectives[0].controls[0].key : null;
          }
          if (this.step !== 'review') {
            this.step = 'mapping';
          }
        } else {
          this.step = 'reading';
          this.startPolling();
        }
      },
      // Rows the AI says aren't controls start switched off
      defaultOff(result) {
        let off = {};
        result.objectives.forEach(objective => objective.controls.forEach(control => {
          if ((control.flags || []).indexOf('not_a_control') !== -1) {
            off[control.key] = true;
          }
        }));
        return off;
      },
      startPolling() {
        if (this.poller) {
          return;
        }
        this.poller = setInterval(() => {
          if (this.importItem) {
            this.load(this.importItem.id);
          }
        }, POLL_MS);
      },
      stopPolling() {
        if (this.poller) {
          clearInterval(this.poller);
          this.poller = null;
        }
      },
      cancelImport() {
        let self = this;
        this.busy = true;
        piincHttp.delete('assessments/' + this.assessment.slug + '/imports/' + this.importItem.id).then(function() {
          self.busy = false;
          self.restart();
        }, function() {
          self.busy = false;
          self.restart();
        });
      },
      changeSettings(settings) {
        this.settings = settings;
      },
      toggle(key) {
        this.$set(this.off, key, !this.off[key]);
      },
      edit(key, values) {
        this.$set(this.edits, key, Object.assign({}, this.edits[key] || {}, values));
      },
      save() {
        let self = this;
        let objectives = payload(this.reviewed);
        this.busy = true;
        this.error = '';
        piincHttp.post('assessments/' + this.assessment.slug + '/imports/' + this.importItem.id + '/save', { objectives: objectives }).then(function(response) {
          self.busy = false;
          remember(self.assessment, null);
          self.$emit('imported', response.data.saved);
          self.close();
          self.restart();
        }, function(error) {
          self.busy = false;
          let data = error.response && error.response.data;
          self.error = data && data.message === 'duplicate_references'
            ? self.$t('audit_plan_import.review.duplicates', { references: data.references.join(', ') })
            : self.errorText(error);
        });
      },
      // The API's messages aren't translated; file type, size and pages are checked here before upload
      errorText(error) {
        let status = error && error.response && error.response.status;
        return this.$t(status === 422 ? 'audit_plan_import.failed.invalid' : 'audit_plan_import.failed.failed');
      }
    },
    beforeDestroy() {
      this.stopPolling();
    }
  }
</script>
